import * as d3 from 'd3';
import { Box2, MathUtils, Vector2, Vector3 } from 'three';
import { max, min } from 'underscore';

import { t } from '../../../../../utils/i18-next';
import { roundToDecimals } from '../../../../../utils/math/number';
import {
  fractionFromSlopePercent,
  slopePercentToDegrees,
  vectorToSlopePercent,
} from '../../../../../utils/math/slope';
import { isDefined } from '../../../../../utils/variables';
import {
  ProfileSectionMeasureMode,
  ProfileSectionTool,
} from '../../../../../viewer/tools/profile-section-tool';
import { getPolyLineLength } from '../../../../../viewer/utilities/math-helpers';
import { ViewerEnvironmentInstance } from '../../../../../viewer/viewer-environment';
import { userStateAdapter } from '../../../../state/user-state/user-state-adapter';
import {
  calculateStepSize,
  floodFillBfs,
  LengthProfileBfsCell,
} from './profile-section-flood-fill-bfs';
import { GraphLine, ProfileSectionLineData } from './profile-section-tool-menu';

type SvgSelection = d3.Selection<SVGGElement, unknown, null, undefined>;
type SvgScale = d3.ScaleLinear<number, number, never>;
type GraphContext = ReturnType<typeof createGraph>;

/**
 * Creates and styles the hover‐dot and label, and returns a function that
 * re‐positions both on demand.  If you pass in a `from` point, it will
 * also append “L: <distance>” to the label.
 */
function createMeasurements(g: SvgSelection) {
  const measureLine = g
    .append('line')
    .attr('class', 'measure-line')
    .attr('stroke', 'orange')
    .attr('stroke-width', 2)
    .style('display', 'none');

  const dot = g
    .append('circle')
    .attr('r', 4)
    .attr('fill', 'yellow')
    .attr('stroke', 'black')
    .attr('stroke-width', 1)
    .style('pointer-events', 'none')
    .style('display', 'none');

  const areaRect = g
    .append('rect')
    .attr('fill', 'rgba(255, 255, 0, 0.3)')
    .attr('stroke', 'black')
    .attr('stroke-width', 1)
    .style('pointer-events', 'none')
    .style('display', 'none');

  const labelGroup = g.append('g').style('pointer-events', 'none').style('display', 'none');

  const labelBackground = labelGroup
    .append('rect')
    .attr('fill', 'white')
    .attr('fill-opacity', '0.8')
    .attr('stroke', 'none')
    .attr('rx', 0)
    .attr('ry', 0);

  const labelText = labelGroup
    .append('text')
    .attr('fill', 'black')
    .attr('font-size', '12px')
    .attr('font-family', 'monospace');

  return function refreshMeasurements(
    zx: SvgScale,
    zy: SvgScale,
    measureMode: ProfileSectionMeasureMode,
    stepSize: number,
    lastCursorPosition: Vector2 | null,
    measureFromPosition: Vector2 | null,
    slope: number = 0,
  ) {
    if (!lastCursorPosition) {
      dot.style('display', 'none');
      areaRect.style('display', 'none');
      labelGroup.style('display', 'none');
      measureLine.style('display', 'none');
      return;
    }

    if (measureFromPosition && lastCursorPosition) {
      measureLine
        .style('display', 'block')
        .attr('x1', zx(measureFromPosition.x))
        .attr('y1', zy(measureFromPosition.y))
        .attr('x2', zx(lastCursorPosition.x))
        .attr('y2', zy(lastCursorPosition.y));
    } else {
      measureLine.style('display', 'none');
    }

    const sx = zx(lastCursorPosition.x);
    const sy = zy(lastCursorPosition.y);

    if (measureMode === 'area') {
      // Compute rectangle corners in data units.
      const dataX0 = lastCursorPosition.x - stepSize / 2;
      const dataX1 = lastCursorPosition.x + stepSize / 2;
      const dataY0 = lastCursorPosition.y - stepSize / 2;
      const dataY1 = lastCursorPosition.y + stepSize / 2;

      // Convert to pixel units.
      const sx0 = zx(dataX0);
      const sx1 = zx(dataX1);
      const sy0 = zy(dataY0);
      const sy1 = zy(dataY1);

      // Compute pixel rect.
      const rectX = Math.min(sx0, sx1);
      const rectY = Math.min(sy0, sy1);
      const rectWidth = Math.abs(sx1 - sx0);
      const rectHeight = Math.abs(sy1 - sy0);

      dot.style('display', 'none');

      areaRect
        .attr('x', rectX)
        .attr('y', rectY)
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .style('display', 'block');
    } else {
      areaRect.style('display', 'none');
      dot.attr('cx', sx).attr('cy', sy).style('display', 'block');
    }

    const rows: string[] = [`H: ${lastCursorPosition.y.toFixed(3)}`];
    if (measureFromPosition) {
      const dist = measureFromPosition.distanceTo(lastCursorPosition);
      rows.push(`L: ${dist.toFixed(3)}`);
    } else {
      const fraction = fractionFromSlopePercent(slope);
      const multiplier = slope > 0 ? 1 : -1;
      fraction.x = roundToDecimals(fraction.x, 2);
      fraction.y = roundToDecimals(fraction.y, 2) * multiplier;
      const angleDegrees = roundToDecimals(slopePercentToDegrees(slope), 2) * multiplier;
      const slopeValue = slope > Number.MAX_SAFE_INTEGER ? '-' : slope.toFixed(2);

      rows.push(
        `${fraction.y.toString()} : ${fraction.x.toString()} (${slopeValue} % / ${angleDegrees}°)`,
      );
    }

    labelGroup.attr('transform', `translate(${sx + 8}, ${sy - 8})`).style('display', 'block');

    // Update text rows
    labelText
      .selectAll('tspan')
      .data(rows)
      .join(
        (enter) =>
          enter
            .append('tspan')
            .attr('x', 0)
            .attr('dy', (d, i) => (i === 0 ? '0em' : '1.2em'))
            .text((d) => d),
        (update) =>
          update
            .attr('x', 0)
            .attr('dy', (d, i) => (i === 0 ? '0em' : '1.2em'))
            .text((d) => d),
        (exit) => exit.remove(),
      );

    // After rendering tspans, calculate bounding box
    requestAnimationFrame(() => {
      const bbox = labelText.node()!.getBBox();
      const padding = 4;

      labelBackground
        .attr('x', bbox.x - padding)
        .attr('y', bbox.y - padding)
        .attr('width', bbox.width + padding * 2)
        .attr('height', bbox.height + padding * 2);
    });
  };
}

function createGraphGrid(
  g: SvgSelection,
  xScale: SvgScale,
  yScale: SvgScale,
  graphSize: GraphSize,
) {
  // define grid generators
  const xGridGen = (scale: d3.ScaleLinear<number, number>) =>
    d3
      .axisBottom(scale)
      .tickSize(-graphSize.innerHeight) // extend ticks upward across plot
      .tickFormat(() => '') // no labels
      .ticks(10); // or pick a suitable tick count

  const yGridGen = (scale: d3.ScaleLinear<number, number>) =>
    d3
      .axisLeft(scale)
      .tickSize(-graphSize.innerWidth) // extend ticks rightward across plot
      .tickFormat(() => '')
      .ticks(10);

  const applyGridStyle = (grid: SvgSelection) =>
    grid
      .selectAll<SVGLineElement, unknown>('line')
      .attr('stroke', '#ccc')
      .attr('stroke-opacity', '0.5')
      .select(function () {
        return this.parentNode as SVGGElement;
      }) // go back up to the <g>
      .select<SVGPathElement>('path')
      .attr('stroke', 'none');

  // draw the grid *before* the axes and data, so it sits underneath
  const xGridG = g
    .append('g')
    .attr('class', 'grid x-grid')
    .attr('transform', `translate(0,${graphSize.innerHeight})`);

  const yGridG = g.append('g').attr('class', 'grid y-grid');

  const refreshGrid = (zx: SvgScale, zy: SvgScale) => {
    xGridG.call(xGridGen(zx));
    applyGridStyle(xGridG);
    yGridG.call(yGridGen(zy));
    applyGridStyle(yGridG);
  };

  refreshGrid(xScale, yScale);

  return refreshGrid;
}

function createCellGrid(g: SvgSelection) {
  // A group to hold all the squares
  const cellsG = g.append('g').attr('class', 'cells-layer');

  const labelsG = g
    .append('g')
    .attr('class', 'labels')
    .attr('transform', 'translate(8,8)') // margin from top-left
    .style('pointer-events', 'none');

  const areaLabel = labelsG
    .append('text')
    .attr('class', 'area-label')
    .attr('fill', 'black')
    .attr('font-size', '12px')
    .attr('font-family', 'monospace')
    .attr('dominant-baseline', 'hanging')
    .text('');

  const canvas = document.createElement('canvas');
  const targetResolution = 2048;
  canvas.width = targetResolution;
  canvas.height = targetResolution;
  const ctx = canvas.getContext('2d')!;

  function refreshAreaLabel(cells: LengthProfileBfsCell[], ghostArea: number, debugMode?: boolean) {
    const totalArea = d3.sum(cells, (d) => d.size * d.size) + ghostArea;

    const text = t('profileSection.estimatedArea', { ns: 'skyview' });
    const { area } = ViewerEnvironmentInstance.getUnitSymbols();
    if (debugMode) {
      areaLabel.text(
        `${text}: ${totalArea.toFixed(5)} ${area} (${cells.length} cells, ${((ghostArea / totalArea) * 100).toFixed(2)}% Ghost area.)`,
      );
    } else {
      areaLabel.text(`${text}: ${totalArea.toFixed(3)} ${area}`);
    }
  }

  function refreshSquares(cells: LengthProfileBfsCell[]) {
    const squares = cellsG
      .selectAll<SVGRectElement, LengthProfileBfsCell>('rect.flood-point')
      .data(cells, (d) => `${d.vertex.x},${d.vertex.y}`);

    // ENTER
    const enterSquares = squares
      .enter()
      .append('rect')
      .attr('class', 'flood-point')
      .attr('fill', 'orange')
      .attr('stroke', 'none');

    // ENTER + UPDATE
    enterSquares
      .merge(
        squares as unknown as d3.Selection<
          SVGRectElement,
          LengthProfileBfsCell,
          SVGGElement,
          unknown
        >,
      )
      .attr('x', (d) => d.vertex.x - d.size / 2)
      .attr('y', (d) => d.vertex.y - d.size / 2)
      .attr('width', (d) => d.size)
      .attr('height', (d) => d.size);

    // EXIT
    squares.exit().remove();
  }

  function refreshRaster(cells: LengthProfileBfsCell[]) {
    cellsG.selectAll('image').remove();

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    // Find the extents of the cells.
    for (const d of cells) {
      const x0 = d.vertex.x - d.size / 2;
      const y0 = d.vertex.y - d.size / 2;
      const x1 = d.vertex.x + d.size / 2;
      const y1 = d.vertex.y + d.size / 2;

      minX = Math.min(minX, x0);
      minY = Math.min(minY, y0);
      maxX = Math.max(maxX, x1);
      maxY = Math.max(maxY, y1);
    }

    const dataWidth = maxX - minX;
    const dataHeight = maxY - minY;
    const aspectRatio = dataHeight / dataWidth;
    canvas.width = targetResolution;
    canvas.height = Math.round(targetResolution * aspectRatio);

    ctx.fillStyle = 'cyan';
    ctx.globalAlpha = 0.8;
    ctx.strokeStyle = 'rgba(0,0,0,0)';

    const scaleX = canvas.width / (maxX - minX);
    const scaleY = canvas.height / (maxY - minY);
    // Draw the cells onto the canvas.
    for (const d of cells) {
      const x0 = (d.vertex.x - d.size / 2 - minX) * scaleX;
      const y0 = (d.vertex.y - d.size / 2 - minY) * scaleY;
      const w = d.size * scaleX;
      const h = d.size * scaleY;
      ctx.fillRect(Math.floor(x0), Math.floor(y0), Math.ceil(w), Math.ceil(h));
    }

    const url = canvas.toDataURL('image/png');

    cellsG
      .append('image')
      .attr('xlink:href', url)
      .attr('x', minX)
      .attr('y', minY)
      .attr('width', dataWidth)
      .attr('height', dataHeight)
      .attr('filter', 'blur(0.001px)');
  }

  return function refreshCells(
    zx: SvgScale,
    zy: SvgScale,
    cells?: LengthProfileBfsCell[] | null,
    ghostArea?: number,
    debugMode?: boolean,
  ) {
    //Clean up.
    if (cells === null || cells?.length === 0) {
      cellsG.selectAll('rect.flood-point').remove();
      cellsG.selectAll('image').remove();
      areaLabel.text('');
      return;
    }

    if (isDefined(cells)) {
      refreshAreaLabel(cells, ghostArea ?? 0, debugMode);

      if (debugMode) {
        refreshSquares(cells);
      } else {
        refreshRaster(cells);
      }
    }

    // Transform the result.
    const scaleX = zx(1) - zx(0);
    const scaleY = zy(1) - zy(0);
    const translateX = zx(0);
    const translateY = zy(0);
    cellsG.attr('transform', `translate(${translateX},${translateY}) scale(${scaleX},${scaleY})`);
  };
}

function createGraphAxices(g: SvgSelection, graphSize: GraphSize) {
  const xAxisG = g.append('g').attr('transform', `translate(0,${graphSize.innerHeight})`);
  const yAxisG = g.append('g');

  return (zx: SvgScale, zy: SvgScale) => {
    xAxisG.call(d3.axisBottom(zx));
    yAxisG.call(d3.axisLeft(zy));
  };
}

function calculateGraphDomains(
  profileLine: Vector3[],
  graphLines: GraphLine[],
  graphSize: GraphSize,
  xOffset: number,
) {
  const profileLineLength = getPolyLineLength(profileLine);
  const zValues = graphLines.flatMap((lines) =>
    lines.segments.flatMap((segment) => segment.original.map((point) => point.z)),
  );
  const heightMax = max(zValues);
  const heightMin = min(zValues);

  const yRange = heightMax - heightMin;
  const dataAspectRatio = profileLineLength / yRange; // real-world ratio
  const screenAspectRatio = graphSize.innerWidth / graphSize.innerHeight;

  if (dataAspectRatio > screenAspectRatio) {
    // Data is wider than screen: expand Y axis range.
    const centerY = (heightMin + heightMax) / 2;
    const newHeightRange = profileLineLength / screenAspectRatio;

    return {
      x: [-xOffset, profileLineLength - xOffset],
      y: [centerY - newHeightRange / 2, centerY + newHeightRange / 2],
    };
  } else {
    // Data is taller than screen: expand X axis range.
    const centerX = profileLineLength / 2 - xOffset;
    const newLength = yRange * screenAspectRatio;

    return {
      x: [centerX - newLength / 2, centerX + newLength / 2],
      y: [heightMin, heightMax],
    };
  }
}

function calculateGraphSize(svgElement: SVGSVGElement, hideAxisLabels: boolean) {
  const bounds = svgElement.getBoundingClientRect();
  const width = bounds.width;
  const height = bounds.height;
  const margin = hideAxisLabels
    ? {
        top: 20,
        right: 5,
        bottom: 20,
        left: 35,
      }
    : {
        top: 20,
        right: 30,
        bottom: 40,
        left: 90,
      };

  return {
    width,
    height,
    innerWidth: width - margin.left - margin.right,
    innerHeight: height - margin.top - margin.bottom,
    margin,
  };
}

type GraphSize = ReturnType<typeof calculateGraphSize>;

export function getSectionLabel(distanceFromStart: number) {
  return `${Math.floor(distanceFromStart / 1000)} / ${(distanceFromStart % 1000).toFixed(3).padStart(7, '0')}`;
}

function createSectionLabel(
  overlayG: d3.Selection<SVGGElement, unknown, null, undefined>,
  xScale: SvgScale,
  yScale: SvgScale,
  distanceFromStart?: number,
) {
  const labelsG = overlayG.append('g').attr('class', 'labels').style('pointer-events', 'none');
  const pad = 8;

  const sectionLabel = labelsG
    .append('text')
    .attr('class', 'section-label')
    .attr('fill', 'black')
    .attr('font-size', '12px')
    .attr('font-family', 'monospace')
    .attr('text-anchor', 'middle');

  if (isDefined(distanceFromStart)) {
    sectionLabel.text(getSectionLabel(distanceFromStart));
  } else {
    sectionLabel.text('');
  }

  // scales’ ranges are in the inner-plot coordinates (already margin-adjusted)
  const [rx0, rx1] = xScale.range();
  const [ry0, ry1] = yScale.range();
  const xMin = Math.min(rx0, rx1);
  const xMax = Math.max(rx0, rx1);
  const yMin = Math.min(ry0, ry1);

  const cx = (xMin + xMax) / 2;
  const y = yMin - pad;

  sectionLabel.attr('x', cx).attr('y', y).attr('dominant-baseline', 'auto');
}

function createVerticalLine(container: d3.Selection<SVGGElement, unknown, null, undefined>) {
  const line = container
    .append('line')
    .attr('class', 'x0-line')
    .attr('stroke', 'black')
    .attr('stroke-width', 1)
    .attr('pointer-events', 'none')
    .attr('shape-rendering', 'crispEdges');

  return function refreshVerticalLine(zx: SvgScale, zy: SvgScale) {
    const x = zx(0);
    const [y0, y1] = zy.range();

    line.attr('x1', x).attr('x2', x).attr('y1', y0).attr('y2', y1);
  };
}

/**
 * Creates the main graph and scales each axis according to the profile line and graph lines.
 */
function createGraph(
  svgElement: SVGSVGElement,
  profileLine: Vector3[],
  graphLines: GraphLine[],
  xOffset: number,
  distanceFromStart?: number,
  hideAxisLabels = false,
) {
  const svg = d3.select(svgElement);

  // Clear any existing zoom listeners
  svg.on('.zoom', null);

  svg.selectAll('*').remove(); // clear previous renders

  const size = calculateGraphSize(svgElement, hideAxisLabels);
  const domains = calculateGraphDomains(profileLine, graphLines, size, xOffset);

  // Create scales
  const xScale = d3.scaleLinear().domain(domains.x).range([0, size.innerWidth]);
  const yScale = d3.scaleLinear().domain(domains.y).range([size.innerHeight, 0]); // invert Y axis (top is max height)

  svg
    .append('defs')
    .append('clipPath')
    .attr('id', 'clip')
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', size.innerWidth)
    .attr('height', size.innerHeight);

  // Create main group
  const g = svg.append('g').attr('transform', `translate(${size.margin.left},${size.margin.top})`);

  // Group for clipped content only
  const clipId = `clip-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const defs = svg.append('defs');
  defs
    .append('clipPath')
    .attr('id', clipId)
    .attr('clipPathUnits', 'userSpaceOnUse') // avoids bbox-based surprises
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', size.innerWidth)
    .attr('height', size.innerHeight);

  const clippedContent = g
    .append('g')
    .attr('class', 'clipped-layer')
    .attr('clip-path', `url(#${clipId})`);

  const refreshAxices = createGraphAxices(g, size);
  const refreshGrid = createGraphGrid(clippedContent, xScale, yScale, size);
  const refreshCells = createCellGrid(clippedContent);
  createSectionLabel(g, xScale, yScale, distanceFromStart);

  const refreshVerticalLine = createVerticalLine(clippedContent);
  const lineSegments = createLineSegments(clippedContent, xScale, yScale, graphLines);

  const refreshMeasurements = createMeasurements(clippedContent);

  refreshAxices(xScale, yScale);

  if (!hideAxisLabels) {
    const lengthUnit = ViewerEnvironmentInstance.getUnitSymbols().length;

    svg
      .append('text')
      .attr('class', 'x-axis-label')
      .attr('x', size.margin.left + size.innerWidth / 2)
      .attr('y', size.margin.top + size.innerHeight + 35)
      .attr('text-anchor', 'middle')
      .text(t('profileSection.distance', { ns: 'skyview', unit: lengthUnit }));

    const yLabelOffset = 50; // increase this to move it further away

    svg
      .append('text')
      .attr('class', 'y-axis-label')
      .attr('text-anchor', 'middle')
      // center vertically within the plotting area, then shift left from the y-axis by yLabelOffset
      .attr(
        'transform',
        `translate(${size.margin.left - yLabelOffset}, ${size.margin.top + size.innerHeight / 2}) rotate(-90)`,
      )
      .text(t('profileSection.height', { ns: 'skyview', unit: lengthUnit }));
  }

  // Base the distance treshold on the smallest line segment on the graph or 0.1, whichever is smaller
  // const fillThreshold = Math.min(0.1, min(projectedSegmentLengths));
  const boundary = new Box2(
    new Vector2(domains.x[0], domains.y[0]),
    new Vector2(domains.x[1], domains.y[1]),
  );

  return {
    g,
    clippedContent,
    svg,
    size,
    domains,
    xScale,
    yScale,
    refreshGrid,
    refreshAxices,
    refreshMeasurements,
    refreshCells,
    refreshVerticalLine,
    bfsMaxFillIterations: 200000,
    boundary,
    lineSegments,
  };
}

/**
 * Creates the line segments visible on the graph and returns them for future processing.
 */
function createLineSegments(
  g: SvgSelection,
  xScale: SvgScale,
  yScale: SvgScale,
  graphLines: GraphLine[],
): ProfileSectionLineData[] {
  const allLineData: ProfileSectionLineData[] = [];

  for (const graphLine of graphLines) {
    const color = d3
      .rgb(graphLine.color.r * 255, graphLine.color.g * 255, graphLine.color.b * 255)
      .formatHex();

    for (const segment of graphLine.segments) {
      allLineData.push({ color, projected: segment.projected, original: segment.original });
    }
  }

  const lineGen = d3
    .line<Vector2>()
    .x((d) => xScale(d.x))
    .y((d) => yScale(d.y));

  const lineGroup = g.append('g').attr('class', 'line-group');

  lineGroup
    .selectAll('.line-path')
    .data(allLineData)
    .enter()
    .append('path')
    .attr('class', 'line-path')
    .attr('fill', 'none')
    .attr('stroke-width', 1)
    .attr('stroke-linecap', 'round')
    .attr('stroke', (d) => d.color)
    .attr('d', (d) => lineGen(d.projected));

  return allLineData;
}

/**
 * Creates the zoom function that allows for zooming and panning in the graph.
 */
function createZoomFunction(
  tool: ProfileSectionTool,
  graph: GraphContext,
  transformRef: React.MutableRefObject<d3.ZoomTransform | null>,
  lastCursorPosition: React.MutableRefObject<Vector2 | null>,
  measureFromPosition: React.MutableRefObject<Vector2 | null>,
) {
  const zoom = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([0.1, 100])
    .translateExtent([
      [-graph.size.width, -graph.size.height],
      [2 * graph.size.width, 2 * graph.size.height],
    ])
    .extent([
      [0, 0],
      [graph.size.width, graph.size.height],
    ])
    .on('zoom', (event) => {
      const transform = event.transform;
      transformRef.current = transform;
      const zx = transform.rescaleX(graph.xScale) as d3.ScaleLinear<number, number>;
      const zy = transform.rescaleY(graph.yScale) as d3.ScaleLinear<number, number>;
      graph.refreshGrid(zx, zy);
      graph.refreshAxices(zx, zy);
      graph.refreshVerticalLine(zx, zy);
      graph.refreshCells(zx, zy);

      // Update lines
      const updatedLineGen = d3
        .line<Vector2>()
        .x((d) => zx(d.x))
        .y((d) => zy(d.y));

      graph.g
        .selectAll<SVGPathElement, ProfileSectionLineData>('.line-path')
        .attr('d', (d) => updatedLineGen(d.projected));

      graph.refreshMeasurements(
        zx,
        zy,
        tool.measureMode,
        calculateStepSize(graph.bfsMaxFillIterations, graph.boundary),
        lastCursorPosition.current,
        measureFromPosition.current,
      );
    });

  graph.svg.call(zoom);

  if (transformRef.current) {
    graph.svg.call((s) => zoom.transform(s, transformRef.current!));
  }
}

function clampToSegment(targetPosition: Vector2, segment: [Vector2, Vector2]) {
  const segmentVector = segment[1].clone().sub(segment[0]);

  if (segmentVector.lengthSq() === 0) {
    return 0;
  }

  const cursorToSegmentVector = targetPosition.clone().sub(segment[0]);
  return MathUtils.clamp(cursorToSegmentVector.dot(segmentVector) / segmentVector.lengthSq(), 0, 1);
}

function intersectPoint2dPerpendicular(
  fromPoint: Vector2,
  segment: [Vector2, Vector2],
): { point: Vector2; t: number } | undefined {
  if (segment[1].x !== segment[0].x) {
    // segment not vertical
    const t = (fromPoint.x - segment[0].x) / (segment[1].x - segment[0].x);
    if (t >= 0 && t <= 1) {
      const vy = segment[0].y + t * (segment[1].y - segment[0].y);
      return { point: new Vector2(fromPoint.x, vy), t };
    }
  }

  if (segment[1].y !== segment[0].y) {
    const t = (fromPoint.y - segment[0].y) / (segment[1].y - segment[0].y);
    if (t >= 0 && t <= 1) {
      const vx = segment[0].x + t * (segment[1].x - segment[0].x);
      return { point: new Vector2(vx, fromPoint.y), t };
    }
  }
}

/**
 * Creates the picking function that allows picking line segments in the graph and measuring
 * the original coordinate.
 */
function createCursorFunction(
  tool: ProfileSectionTool,
  graph: GraphContext,
  transformRef: React.MutableRefObject<d3.ZoomTransform | null>,
  lastCursorPosition: React.MutableRefObject<Vector2 | null>,
  measureFromPosition: React.MutableRefObject<Vector2 | null>,
) {
  const hover = graph.g
    .append('rect')
    .attr('class', 'hover-capture')
    .attr('width', graph.size.innerWidth)
    .attr('height', graph.size.innerHeight)
    .attr('fill', 'transparent')
    .style('cursor', 'crosshair');

  hover
    .on('mousemove', function (event: MouseEvent) {
      const transform = transformRef.current ?? d3.zoomIdentity;
      const zx = transform.rescaleX(graph.xScale);
      const zy = transform.rescaleY(graph.yScale);

      const [mx, my] = d3.pointer(event, graph.g.node());
      const gx = zx.invert(mx);
      const gy = zy.invert(my);

      const cursorPosition = new Vector2(gx, gy);
      const stepSize = calculateStepSize(graph.bfsMaxFillIterations, graph.boundary);

      if (tool.measureMode === 'distance') {
        /**
         * Limit to perpendicular measuring when shift is held down.
         */
        const limitToPerpendicular = event.shiftKey;
        const fromPoint = measureFromPosition.current;

        // Find nearest point on any segment.
        let nearestDist = Infinity;
        let nearestProjectedPt: Vector2 | null = null;
        let nearestOriginalPt: Vector3 | null = null;
        let nearestSegment: [Vector2, Vector2] | null = null;

        if (limitToPerpendicular && fromPoint) {
          for (const { projected, original } of graph.lineSegments) {
            const intersection = intersectPoint2dPerpendicular(fromPoint, projected);

            if (!intersection) {
              continue;
            }

            const d2 = intersection.point.distanceToSquared(new Vector2(gx, gy));

            if (d2 < nearestDist) {
              nearestDist = d2;
              nearestProjectedPt = intersection.point;
              nearestOriginalPt = original[0].clone().lerp(original[1], intersection.t);
              nearestSegment = projected;
            }
          }
        } else {
          for (const { projected, original } of graph.lineSegments) {
            const t = clampToSegment(cursorPosition, projected);
            const closestPoint = projected[0].clone().lerp(projected[1], t);
            const d2 = closestPoint.distanceToSquared(cursorPosition);
            if (d2 < nearestDist) {
              nearestDist = d2;
              nearestProjectedPt = closestPoint;
              nearestOriginalPt = original[0].clone().lerp(original[1], t);
              nearestSegment = projected;
            }
          }
        }

        if (nearestProjectedPt && nearestOriginalPt && nearestSegment) {
          tool.setCursorPosition(nearestOriginalPt);
          lastCursorPosition.current = nearestProjectedPt.clone();

          const v =
            nearestSegment[0].x < nearestSegment[1].x
              ? nearestSegment[1].clone().sub(nearestSegment[0]).normalize()
              : nearestSegment[0].clone().sub(nearestSegment[1]).normalize();

          const slope = v.x === 0 ? Infinity : vectorToSlopePercent(new Vector3(v.x, 0, v.y));

          graph.refreshMeasurements(
            zx,
            zy,
            tool.measureMode,
            stepSize,
            lastCursorPosition.current,
            measureFromPosition.current,
            slope,
          );
        } else {
          tool.setCursorPosition(undefined);
        }
      } else if (tool.measureMode === 'area') {
        measureFromPosition.current = null;
        lastCursorPosition.current = cursorPosition.clone();
        graph.refreshMeasurements(
          zx,
          zy,
          tool.measureMode,
          stepSize,
          lastCursorPosition.current,
          null,
        );
      }
    })
    .on('mouseleave', () => {
      const transform = transformRef.current ?? d3.zoomIdentity;
      const zx = transform.rescaleX(graph.xScale);
      const zy = transform.rescaleY(graph.yScale);
      graph.refreshMeasurements(
        zx,
        zy,
        tool.measureMode,
        calculateStepSize(graph.bfsMaxFillIterations, graph.boundary),
        null,
        null,
      );
    })
    .on('click', function (event: MouseEvent) {
      event.preventDefault();

      const stepSize = calculateStepSize(graph.bfsMaxFillIterations, graph.boundary);

      if (lastCursorPosition.current) {
        if (tool.measureMode === 'distance') {
          measureFromPosition.current = lastCursorPosition.current.clone();
          const transform = transformRef.current ?? d3.zoomIdentity;
          const zx = transform.rescaleX(graph.xScale);
          const zy = transform.rescaleY(graph.yScale);
          graph.refreshMeasurements(
            zx,
            zy,
            tool.measureMode,
            stepSize,
            lastCursorPosition.current,
            measureFromPosition.current,
          );
        } else if (tool.measureMode === 'area') {
          const floodFillResult = floodFillBfs(
            lastCursorPosition.current,
            graph.lineSegments,
            graph.boundary,
            stepSize,
          );

          const transform = transformRef.current ?? d3.zoomIdentity;
          const zx = transform.rescaleX(graph.xScale);
          const zy = transform.rescaleY(graph.yScale);

          const debugMode = event.shiftKey && userStateAdapter.getSnapshot().isOrganizationAdmin;

          graph.refreshCells(zx, zy, floodFillResult.cells, floodFillResult.ghostArea, debugMode);
        }
      }
    })
    .on('contextmenu', function (event) {
      event.preventDefault();

      const transform = transformRef.current ?? d3.zoomIdentity;
      const zx = transform.rescaleX(graph.xScale);
      const zy = transform.rescaleY(graph.yScale);

      if (tool.measureMode === 'distance') {
        measureFromPosition.current = null;
        graph.refreshMeasurements(
          zx,
          zy,
          tool.measureMode,
          calculateStepSize(graph.bfsMaxFillIterations, graph.boundary),
          lastCursorPosition.current,
          measureFromPosition.current,
        );
      } else if (tool.measureMode === 'area') {
        graph.refreshCells(zx, zy, null);
      }
    })
    .on('wheel', function (event: WheelEvent) {
      if (event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        const newValue =
          event.deltaY > 0 ? graph.bfsMaxFillIterations * 1.1 : graph.bfsMaxFillIterations / 1.1;

        const minIterations = 10_000;
        const maxIterations = 10_000_000;
        graph.bfsMaxFillIterations = MathUtils.clamp(newValue, minIterations, maxIterations);

        const transform = transformRef.current ?? d3.zoomIdentity;
        const zx = transform.rescaleX(graph.xScale);
        const zy = transform.rescaleY(graph.yScale);

        graph.refreshMeasurements(
          zx,
          zy,
          tool.measureMode,
          calculateStepSize(graph.bfsMaxFillIterations, graph.boundary),
          lastCursorPosition.current,
          measureFromPosition.current,
        );
      }
    });
}

export const profileSectionGraphUtils = {
  createGraph,
  createZoomFunction,
  createCursorFunction,
};
